import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useThemeColor } from '../../context/BrandingContext';

export interface TopAgentDatum {
  agentName: string;
  conversations: number;
}

interface TopAgentsChartProps {
  data: TopAgentDatum[];
}

/** Truncates a long agent name for the X-axis tick without cutting mid-word where possible. */
function truncateLabel(name: string, max = 14): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

export const TopAgentsChart: React.FC<TopAgentsChartProps> = ({ data }) => {
  // Chart props need real colours, not classes; this follows the theme.
  const themeColor = useThemeColor();
  const chartData = data.slice(0, 5).map((d) => ({ ...d, label: truncateLabel(d.agentName) }));

  return (
    <article className="flex h-full min-w-0 min-h-[420px] flex-col rounded-card bg-white p-4 shadow-[0_20px_45px_rgb(var(--c-1c2350,28_35_80)/0.08)] sm:p-5 lg:p-6">
      <div className="flex min-w-0 items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-title-md font-semibold text-c-1a1b2f sm:text-title-lg">Top Active Agents</h3>
          <p className="text-sm text-c-747a9d">Conversations handled, by agent</p>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-center text-sm text-c-8f95b4">
          No agent activity data yet — this fills in once your agents start handling conversations.
        </div>
      ) : (
        <div className="mt-4 min-w-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid stroke={themeColor('#edf0fb')} vertical={false} />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={{ fill: themeColor('#8b90af'), fontSize: 12 }}
              />
              <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: themeColor('#8b90af'), fontSize: 12 }} />
              <Tooltip
                cursor={{ fill: 'rgba(96,113,236,0.08)' }}
                formatter={(value: number) => [value, 'Conversations']}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.agentName ?? ''}
                contentStyle={{ borderRadius: 12, border: `1px solid ${themeColor('#edf0fb')}`, fontSize: 13 }}
              />
              <Bar dataKey="conversations" fill={themeColor('#6071ec')} radius={[8, 8, 0, 0]} maxBarSize={56} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
};

export default TopAgentsChart;
